import React, { useState } from 'react';
import type { User } from '@lnr-v2/types';
import { Avatar, Icon, Badge } from '@lnr-v2/ui';
import styles from './UsersTable.module.css';

export interface UsersTableProps {
  users: User[];
  selectedUsers: string[];
  onSelectUser: (userId: string) => void;
  onSelectAll: () => void;
  onEdit: (user: User) => void;
  onDelete: (user: User) => void;
  loading?: boolean;
}

export const UsersTable: React.FC<UsersTableProps> = ({
  users,
  selectedUsers,
  onSelectUser,
  onSelectAll,
  onEdit,
  onDelete,
  loading = false,
}) => {
  const [sortColumn, setSortColumn] = useState<keyof User>('updatedAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const allSelected = users.length > 0 && selectedUsers.length === users.length;
  const someSelected = selectedUsers.length > 0 && !allSelected;

  const handleSort = (column: keyof User) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400)
      return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800)
      return `${Math.floor(diffInSeconds / 86400)}d ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  const getRoleBadgeVariant = (role: User['role']) => {
    switch (role) {
      case 'admin':
        return 'danger';
      case 'editor':
        return 'primary';
      case 'viewer':
        return 'neutral';
      default:
        return 'neutral';
    }
  };

  const getStatusBadgeVariant = (status: User['status']) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'neutral';
      case 'pending':
        return 'warning';
      default:
        return 'neutral';
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner} />
        <p>Loading users...</p>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className={styles.emptyState}>
        <h3>No users found</h3>
        <p>Try adjusting your filters or create a new user</p>
      </div>
    );
  }

  return (
    <div className={styles.tableContainer}>
      <table className={styles.table}>
        <thead className={styles.thead}>
          <tr>
            <th className={styles.checkboxCell}>
              <input
                type="checkbox"
                checked={allSelected}
                ref={(input) => {
                  if (input) {
                    input.indeterminate = someSelected;
                  }
                }}
                onChange={onSelectAll}
                className={styles.checkbox}
              />
            </th>
            <th
              className={styles.th}
              onClick={() => handleSort('name')}
              role="button"
            >
              <div className={styles.headerCell}>
                <span>User</span>
                {sortColumn === 'name' && (
                  <Icon
                    name={
                      sortDirection === 'asc' ? 'chevron-up' : 'chevron-down'
                    }
                    size="sm"
                  />
                )}
              </div>
            </th>
            <th
              className={styles.th}
              onClick={() => handleSort('role')}
              role="button"
            >
              <div className={styles.headerCell}>
                <span>Role</span>
                {sortColumn === 'role' && (
                  <Icon
                    name={
                      sortDirection === 'asc' ? 'chevron-up' : 'chevron-down'
                    }
                    size="sm"
                  />
                )}
              </div>
            </th>
            <th
              className={styles.th}
              onClick={() => handleSort('status')}
              role="button"
            >
              <div className={styles.headerCell}>
                <span>Status</span>
                {sortColumn === 'status' && (
                  <Icon
                    name={
                      sortDirection === 'asc' ? 'chevron-up' : 'chevron-down'
                    }
                    size="sm"
                  />
                )}
              </div>
            </th>
            <th
              className={styles.th}
              onClick={() => handleSort('lastLoginAt')}
              role="button"
            >
              <div className={styles.headerCell}>
                <span>Last Login</span>
                {sortColumn === 'lastLoginAt' && (
                  <Icon
                    name={
                      sortDirection === 'asc' ? 'chevron-up' : 'chevron-down'
                    }
                    size="sm"
                  />
                )}
              </div>
            </th>
            <th className={styles.th}>
              <span>Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className={styles.tbody}>
          {users.map((user) => (
            <tr
              key={user.id}
              className={`${styles.tr} ${
                selectedUsers.includes(user.id) ? styles.selected : ''
              }`}
            >
              <td className={styles.checkboxCell}>
                <input
                  type="checkbox"
                  checked={selectedUsers.includes(user.id)}
                  onChange={() => onSelectUser(user.id)}
                  className={styles.checkbox}
                />
              </td>
              <td className={styles.td}>
                <div className={styles.userCell}>
                  <Avatar
                    name={user.name}
                    src={user.avatar}
                    size="md"
                    status={user.status === 'active' ? 'online' : undefined}
                    glass
                  />
                  <div className={styles.userInfo}>
                    <div className={styles.userName}>{user.name}</div>
                    <div className={styles.userEmail}>{user.email}</div>
                  </div>
                </div>
              </td>
              <td className={styles.td}>
                <Badge variant={getRoleBadgeVariant(user.role)} glass>
                  {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                </Badge>
              </td>
              <td className={styles.td}>
                <Badge variant={getStatusBadgeVariant(user.status)} glass>
                  {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                </Badge>
              </td>
              <td className={styles.td}>
                <span className={styles.dateText}>
                  {formatDate(user.lastLoginAt)}
                </span>
              </td>
              <td className={styles.td}>
                <div className={styles.actions}>
                  <button
                    onClick={() => onEdit(user)}
                    className={styles.actionButton}
                    title="Edit user"
                  >
                    <Icon name="edit" size="sm" />
                  </button>
                  <button
                    onClick={() => onDelete(user)}
                    className={`${styles.actionButton} ${styles.deleteButton}`}
                    title="Delete user"
                  >
                    <Icon name="trash" size="sm" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
